import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, Card, CardContent, Divider, Grid, Button, IconButton, Alert, Chip, Link, Skeleton, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { TextField } from "@mui/material";

import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { adjustTimes, getStopDepartureDatetime, getStopArrivalDatetime, getSegmentDurationMinutes } from '../utils/DateTime';
import { getCityDisplay, getCityKey, cityMatchesSearch } from '../utils/city';
import MapAddressLink from '../components/MapAddressLink';
import PaymentButton from '../components/PaymentButton';
import BaggageOptions from '../components/BaggageOptions';
import DirectionJourneySummary, {
  formatDurationHhMm,
  formatHumanLayover,
} from '../components/DirectionJourneySummary';
import { useNavigate } from "react-router-dom";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CircleIcon from "@mui/icons-material/Circle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { MuiTelInput, matchIsValidTel } from "mui-tel-input";
import { useAuthUser } from "../api/users";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { useTranslation } from "react-i18next";
import {bookTicket, bookItinerary, bookPartnerTicket, bookRoundTrip, createPayment, getPartnerRouteInfo} from '../api';
import { getSeatsForTrip } from "../api/trips";

import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";

// NEW: imports for payment method UI
import { FormControl, FormLabel, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import StorefrontIcon from "@mui/icons-material/Storefront";
import StarIcon from "@mui/icons-material/Star";
import LuggageIcon from "@mui/icons-material/Luggage";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { useBrand, useBrandColors } from "../brand/BrandContext";
import {
  encodeBase64Unicode,
  decodeBase64Unicode,
  getLegStopsSlice,
  getDirectionSummary,
  StopTimelineRow,
} from "./checkout/checkoutHelpers";
import { useCheckoutPromo } from "./checkout/useCheckoutPromo";

const CheckoutPage = () => {
  const { t } = useTranslation("common");
  const brand = useBrand();
  const BRAND = useBrandColors();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    trip,
    itinerary,
    type: bookingType = "direct",
    connection,
    fromCity,
    toCity,
    adults,
    children,
    outbound,
    returnTrip,
  } = location.state || {};
  const isTransfer = bookingType === "transfer" && itinerary;
  const isPartner = bookingType === "partner" && connection;
  const isRoundTrip =
    bookingType === "round_trip" &&
    outbound &&
    returnTrip &&
    (outbound.trip || outbound.itinerary) &&
    (returnTrip.trip || returnTrip.itinerary);

  const outboundSummary = isRoundTrip ? getDirectionSummary(outbound) : null;
  const returnSummary = isRoundTrip ? getDirectionSummary(returnTrip) : null;

  const rtDirectionTrip = (sel) =>
    sel?.trip || sel?.itinerary?.legs?.[0]?.trip || null;

  const leg1 = isTransfer ? (itinerary.legs?.find((l) => l.leg_index === 1) ?? itinerary.legs?.[0]) : null;
  const leg2 = isTransfer ? (itinerary.legs?.find((l) => l.leg_index === 2) ?? itinerary.legs?.[1]) : null;
  const activeTrip = isRoundTrip
    ? rtDirectionTrip(outbound)
    : isTransfer
      ? leg1?.trip
      : trip;

  if (
    (!trip && !isTransfer && !isPartner && !isRoundTrip) ||
    location.state === null
  ) {
    navigate("/");
  }

  const initialAdults = adults ?? 1;
  const initialChildren = children ?? 0;
  const initialTotal = initialAdults + initialChildren;

  const [passengerData, setPassengerData] = useState(() =>
    [
      ...Array.from({ length: initialAdults }, () => ({ name: "", surname: "", type: "adult", birthDate: null })),
      ...Array.from({ length: initialChildren }, () => ({ name: "", surname: "", type: "child", birthDate: null })),
    ]
  );

  const [passengerErrors, setPassengerErrors] = useState(() =>
    Array.from({ length: initialTotal }, () => ({ name: false, surname: false, birthDate: false }))
  );
  
  const [buyerPhone, setBuyerPhone] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [emailError, setEmailError] = useState("");

  const [showAllStops, setShowAllStops] = useState(false);

  const [, setBookingId] = useState(null);
  const [isBookingPending, setIsBookingPending] = useState(false);
  const bookingIdempotencyKeyRef = useRef(null);

  const [seats, setSeats] = useState([]);
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [errorDialogText, setErrorDialogText] = useState("");

  // NEW: payment method — 'card' | 'on_site'
  const isPaymentEnabled = process.env.REACT_APP_PAYMENT_ENABLED === "true";
  const cardAvailable = isPaymentEnabled && brand.features?.card_payment === true;
  const [paymentMethod, setPaymentMethod] = useState(
    brand.features?.card_payment ? "card" : "on_site"
  );

  useEffect(() => {
    if (!cardAvailable && paymentMethod === "card") {
      setPaymentMethod("on_site");
    }
  }, [cardAvailable, paymentMethod]);

  const [partnerSchedule, setPartnerSchedule] = useState([]);
  const [partnerCarrierInfo, setPartnerCarrierInfo] = useState(null);
  const [partnerDetailsLoading, setPartnerDetailsLoading] = useState(false);

  const toggleStops = () => setShowAllStops(!showAllStops);

  const { user } = useAuthUser();

  const promoPassengerKey = `${passengerData.length}:${passengerData
    .map((p) => `${p.type}:${p.birthDate || ""}`)
    .join(",")}`;

  const {
    promoInput,
    setPromoInput,
    appliedPromo,
    promoError,
    promoLoading,
    promoDiscount,
    handleApplyPromo: applyPromo,
    handleClearPromo,
  } = useCheckoutPromo({
    isPartner,
    isRoundTrip,
    isTransfer,
    trip,
    fromCity,
    toCity,
    passengerKey: promoPassengerKey,
    paymentMethod,
  });

  useEffect(() => {
    if (user?.email) setBuyerEmail(user.email);
    if (user?.phone_number) setBuyerPhone(user.phone_number);
    if (user?.first_name && user?.last_name){
      const data = [...passengerData];
      if (data.length > 0){
        data[0]['name'] = user?.first_name;
        data[0]['surname'] = user?.last_name;
        data[0]['type'] = "adult";
        setPassengerData(data);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const fetchSeats = async () => {
      if (isPartner) {
        setSeats([]);
        return;
      }
      if (activeTrip?.id) {
        const data = await getSeatsForTrip(activeTrip.id);
        setSeats(data.map((s) => ({
          id: String(s.seat_number),
          row: Math.floor((s.seat_number - 1) / 4) + 1,
          column: ((s.seat_number - 1) % 4) + 1,
          status: s.status,
          type: "classic"
        })));
      }
    };
    fetchSeats();
  }, [activeTrip?.id, isPartner]);

  useEffect(() => {
    let cancelled = false;
    const loadPartnerDetails = async () => {
      if (!isPartner || !connection?.data_bundle) {
        setPartnerSchedule([]);
        setPartnerCarrierInfo(null);
        return;
      }
      setPartnerDetailsLoading(true);
      const data = await getPartnerRouteInfo(connection.data_bundle);
      if (cancelled) return;
      setPartnerSchedule(Array.isArray(data?.schedule) ? data.schedule : []);
      setPartnerCarrierInfo(data?.carrier || null);
      setPartnerDetailsLoading(false);
    };
    loadPartnerDetails();
    return () => {
      cancelled = true;
    };
  }, [isPartner, connection?.data_bundle]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const departureDateTime = isPartner
    ? dayjs(
        `${(connection.date || "").split(".").reverse().join("-")}T${connection.departure_time || "00:00"}`
      )
    : dayjs(activeTrip?.departure_date);
  const stops = adjustTimes(departureDateTime, activeTrip?.route?.stops ?? []);

  const firstStopIndex = isTransfer
    ? 0
    : stops?.findIndex((stop) => cityMatchesSearch(stop.city, fromCity));
  const lastStopIndex = isTransfer
    ? stops.length - 1
    : stops?.findIndex((stop) => cityMatchesSearch(stop.city, toCity));

  const [isPaymentDisabled, setIsPaymentDisabled] = useState(true);

  // Вік на дату рейсу; дитина < 6 років — знижка, інакше як дорослий
  const getAgeAtTrip = (birthDate) => {
    if (!birthDate) return null;
    const d = dayjs(birthDate);
    const ref = departureDateTime;
    return ref.diff(d, "year", true);
  };

  const getEffectiveType = (p) => {
    if (p.type !== "child") return p.type || "adult";
    const age = getAgeAtTrip(p.birthDate);
    if (age == null) return "child";
    return age < 6 ? "child" : "adult";
  };

  useEffect(() => {
    const arePassengersValid = passengerData.every((passenger) => {
      const nameOk = passenger.name.trim().length >= 2 && passenger.surname.trim().length >= 2;
      const childBirthOk = passenger.type !== "child" || (passenger.birthDate && getAgeAtTrip(passenger.birthDate) != null);
      return nameOk && childBirthOk;
    });

    const effectiveCountsForValidation = passengerData.reduce(
      (acc, p) => {
        const t = getEffectiveType(p);
        if (t === "adult") acc.adults += 1; else acc.children += 1;
        return acc;
      },
      { adults: 0, children: 0 }
    );
    const hasAdultWithChild = effectiveCountsForValidation.children === 0 || effectiveCountsForValidation.adults >= 1;

    const isBuyerPhoneValid = buyerPhone && matchIsValidTel(buyerPhone);
    const isBuyerEmailValid = buyerEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(buyerEmail);

    setIsPaymentDisabled(!(arePassengersValid && isBuyerPhoneValid && isBuyerEmailValid && hasAdultWithChild));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passengerData, buyerPhone, buyerEmail]);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has("form")) {
      url.searchParams.delete("form");
      window.history.replaceState({}, "", url);
    }
  }, []);

  // Перший пасажир завжди дорослий (супроводжувач)
  const firstPassengerType = passengerData[0]?.type;
  useEffect(() => {
    if (passengerData.length > 0 && firstPassengerType !== "adult") {
      setPassengerData((prev) => [{ ...prev[0], type: "adult", birthDate: null }, ...prev.slice(1)]);
      setPassengerErrors((prev) => [{ ...prev[0], birthDate: false }, ...prev.slice(1)]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstPassengerType]);

  useEffect(() => {
    const formParam = new URLSearchParams(location.search).get("form");
    if (formParam) {
      try {
        const decoded = decodeBase64Unicode(formParam);
        const parsed = JSON.parse(decoded);
        if (parsed?.passengers && Array.isArray(parsed.passengers)) {
          const normalized = parsed.passengers.map((p, i) => ({
            name: p.name || "",
            surname: p.surname || "",
            type: i === 0 ? "adult" : (p.type === "child" ? "child" : "adult"),
            birthDate: i === 0 ? null : (p.birthDate ? dayjs(p.birthDate) : (p.birth_date ? dayjs(p.birth_date) : null)),
          }));
          setPassengerData(normalized);
          setPassengerErrors(Array.from({ length: normalized.length }, () => ({ name: false, surname: false, birthDate: false })));
        }
        if (parsed?.email) setBuyerEmail(parsed.email);
        if (parsed?.phone) setBuyerPhone(parsed.phone);
      } catch {}
    }
  }, [location.search]);

  if (
    !isPartner &&
    !isTransfer &&
    !isRoundTrip &&
    (firstStopIndex === -1 || lastStopIndex === -1 || firstStopIndex >= lastStopIndex)
  ) {
    return null;
  }
  const firstStop = isTransfer || isPartner || isRoundTrip ? null : stops?.[firstStopIndex];
  const lastStop = isTransfer || isPartner || isRoundTrip ? null : stops?.[lastStopIndex];

  const totalDurationMinutes = isPartner
    ? Math.round(Number(connection.travel_time || 0) / 60)
    : isTransfer
      ? itinerary.total_duration_minutes
      : isRoundTrip
        ? null
      : getSegmentDurationMinutes(firstStop, lastStop);

  if (!isPartner && !isTransfer && !isRoundTrip && totalDurationMinutes == null) {
    return null;
  }
  const totalHours = Math.floor((totalDurationMinutes || 0) / 60);
  const totalMinutes = (totalDurationMinutes || 0) % 60;
  const totalDurationText = t("units.hoursShort", {
    duration: `${String(totalHours).padStart(2, "0")}:${String(totalMinutes).padStart(2, "0")}`,
  });

  const leg1StopsSlice = isTransfer ? getLegStopsSlice(leg1) : null;
  const leg2StopsSlice = isTransfer ? getLegStopsSlice(leg2) : null;
  const rtIntermediateStopsCount = (() => {
    if (!isRoundTrip) return 0;
    let count = 0;
    for (const sel of [outbound, returnTrip]) {
      if (sel?.itinerary?.legs?.length) {
        for (const leg of sel.itinerary.legs) {
          const slice = getLegStopsSlice(leg)?.slice;
          count += Math.max((slice?.length ?? 0) - 2, 0);
        }
      } else {
        const summary = getDirectionSummary(sel);
        count += Math.max((summary?.stopsSlice?.length ?? 0) - 2, 0);
      }
    }
    return count;
  })();
  const intermediateStopsCount = isPartner
    ? Math.max(partnerSchedule.length - 2, 0)
    : isTransfer
      ? Math.max((leg1StopsSlice?.slice.length ?? 0) - 2, 0) +
        Math.max((leg2StopsSlice?.slice.length ?? 0) - 2, 0)
      : isRoundTrip
        ? rtIntermediateStopsCount
      : Math.max((stops?.slice(firstStopIndex, lastStopIndex + 1)?.length ?? 0) - 2, 0);

  const partnerAmenities =
    partnerCarrierInfo?.amenities?.length
      ? partnerCarrierInfo.amenities
      : connection?.carrier_amenities || [];
  const ownAmenities = (() => {
    if (isPartner) return [];
    if (isTransfer) {
      const a = leg1?.trip?.bus?.amenities || [];
      const b = leg2?.trip?.bus?.amenities || [];
      return [...new Set([...a, ...b])];
    }
    if (isRoundTrip) {
      const collect = (sel) => {
        if (sel?.itinerary?.legs?.length) {
          return sel.itinerary.legs.flatMap((l) => l.trip?.bus?.amenities || []);
        }
        return sel?.trip?.bus?.amenities || [];
      };
      return [...new Set([...collect(outbound), ...collect(returnTrip)])];
    }
    return trip?.bus?.amenities || [];
  })();
  const partnerBaggage =
    partnerCarrierInfo?.baggage || connection?.baggage || "";
  const partnerCarrierName =
    partnerCarrierInfo?.name || connection?.carrier || "ContraBUS";
  const partnerLogo =
    partnerCarrierInfo?.logo_url || connection?.carrier_logo_url || "";
  const partnerRating =
    partnerCarrierInfo?.rating ?? connection?.carrier_rating;
  const partnerComfLink =
    partnerCarrierInfo?.comf_link || connection?.carrier_comf_link || "";

  const renderVisibleStops = (slice) => {
    if (!slice?.length) return null;
    const visibleStops = showAllStops ? slice : [slice[0], slice[slice.length - 1]];
    return visibleStops.map((stop, index) => (
      <StopTimelineRow
        key={`${getCityKey(stop.city)}-${index}`}
        stop={stop}
        isLast={index === visibleStops.length - 1}
        t={t}
      />
    ));
  };

  const formatLayoverDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) return `${hours}:${String(mins).padStart(2, "0")}`;
    if (hours > 0) return `${hours}:00`;
    return `0:${String(mins).padStart(2, "0")}`;
  };

  const currencyRate = isTransfer
    ? (itinerary.currency_rate ?? leg1?.trip?.currency_rate ?? 52)
      : isRoundTrip
      ? (rtDirectionTrip(outbound)?.currency_rate ?? 52)
    : trip?.currency_rate;

  const unitPriceUah = (fare, rate) => {
    if (fare?.price_uah != null) return Number(fare.price_uah);
    return Number(fare?.price ?? 0) * (rate ?? 1);
  };
  const unitChildUah = (fare, rate) => {
    if (fare?.child_price_uah != null) return Number(fare.child_price_uah);
    if (fare?.price_uah != null) return Number(fare.price_uah);
    return Number(fare?.child_price ?? 0) * (rate ?? 1);
  };

  const directHasCampaign =
    !isPartner &&
    !isTransfer &&
    !isRoundTrip &&
    trip?.fare?.price_uah != null &&
    trip?.fare?.list_price_uah != null;
  const campaignApplies =
    directHasCampaign &&
    !(trip?.fare?.campaign_online_only && paymentMethod !== "card");

  const rtDirectionAdultUah = (sel) => {
    if (sel?.itinerary?.total_price_uah != null) {
      return Number(sel.itinerary.total_price_uah);
    }
    if (sel?.trip) {
      return unitPriceUah(sel.trip.fare, sel.trip.currency_rate);
    }
    return 0;
  };
  const rtDirectionChildUah = (sel) => {
    if (sel?.itinerary) {
      const legs = sel.itinerary.legs || [];
      return legs.reduce((sum, leg) => {
        const rate = sel.itinerary.currency_rate ?? currencyRate;
        return (
          sum +
          Number(
            leg?.fare?.child_price_uah ??
              (leg?.fare?.child_price ?? 0) * rate
          )
        );
      }, 0);
    }
    if (sel?.trip) {
      return unitChildUah(sel.trip.fare, sel.trip.currency_rate);
    }
    return 0;
  };

  const adultUnitUah = isPartner
    ? Number(connection.price || 0)
    : isTransfer
      ? (Number(leg1?.fare?.price_uah ?? (leg1?.fare?.price ?? 0) * currencyRate) +
          Number(leg2?.fare?.price_uah ?? (leg2?.fare?.price ?? 0) * currencyRate))
      : isRoundTrip
        ? rtDirectionAdultUah(outbound) + rtDirectionAdultUah(returnTrip)
      : campaignApplies
        ? Number(trip.fare.price_uah)
        : Number(trip?.fare?.price ?? 0) * currencyRate;

  const childUnitUah = isPartner
    ? Number(connection.price || 0)
    : isTransfer
      ? (Number(leg1?.fare?.child_price_uah ?? (leg1?.fare?.child_price ?? 0) * currencyRate) +
          Number(leg2?.fare?.child_price_uah ?? (leg2?.fare?.child_price ?? 0) * currencyRate))
      : isRoundTrip
        ? rtDirectionChildUah(outbound) + rtDirectionChildUah(returnTrip)
        : campaignApplies
          ? Number(trip.fare.child_price_uah ?? trip.fare.price_uah)
          : Number(trip?.fare?.child_price ?? 0) * currencyRate;

  const adultFareTotal = adultUnitUah;
  const childFareTotal = childUnitUah;
  const discountChildCount = passengerData.filter((p) => {
    if (p.type !== "child" || !p.birthDate) return false;
    const age = getAgeAtTrip(p.birthDate);
    return age != null && age < 6;
  }).length;
  const childDiscountUah =
    discountChildCount > 0 && adultUnitUah > childUnitUah
      ? Math.round((adultUnitUah - childUnitUah) * discountChildCount * 100) / 100
      : 0;

  const effectiveCounts = passengerData.reduce(
    (acc, p) => {
      const t = getEffectiveType(p);
      if (t === "adult") acc.adults += 1; else acc.children += 1;
      return acc;
    },
    { adults: 0, children: 0 }
  );

  const rtOutboundAmount = isRoundTrip
    ? Math.round(
        (effectiveCounts.adults * rtDirectionAdultUah(outbound) +
          effectiveCounts.children * rtDirectionChildUah(outbound)) *
          100
      ) / 100
    : 0;
  const rtReturnAmount = isRoundTrip
    ? Math.round(
        (effectiveCounts.adults * rtDirectionAdultUah(returnTrip) +
          effectiveCounts.children * rtDirectionChildUah(returnTrip)) *
          100
      ) / 100
    : 0;

  const baseAmount = isPartner
    ? Math.round(Number(connection.price || 0) * passengerData.length * 100) / 100
    : isTransfer
      ? Math.round((itinerary.total_price_uah ?? 0) * 100) / 100
      : Math.round(
          (effectiveCounts.adults * adultUnitUah +
            effectiveCounts.children * childUnitUah) *
            100
        ) / 100;

  const listAmount =
    !isPartner && !isTransfer && !isRoundTrip && campaignApplies
      ? Math.round(
          (effectiveCounts.adults * Number(trip.fare.list_price_uah) +
            effectiveCounts.children *
              Number(trip.fare.list_child_price_uah ?? trip.fare.list_price_uah)) *
            100
        ) / 100
      : null;

  const promoWins = promoDiscount > 0;
  const campaignSavings =
    listAmount != null && !promoWins && listAmount > baseAmount
      ? Math.round((listAmount - baseAmount) * 100) / 100
      : 0;
  const pricedAtList = campaignSavings > 0 || (promoWins && listAmount != null);
  const shownAdultUnit = pricedAtList
    ? Number(trip.fare.list_price_uah)
    : adultFareTotal;
  const shownChildUnit = pricedAtList
    ? Number(trip.fare.list_child_price_uah ?? trip.fare.list_price_uah)
    : childFareTotal;
  const fullPriceAmount = promoWins ? (listAmount ?? baseAmount) : listAmount;

  const totalAmount = promoWins
    ? Math.round(((listAmount ?? baseAmount) - promoDiscount) * 100) / 100
    : baseAmount;

  const handleApplyPromo = () =>
    applyPromo({ baseAmount, effectiveCounts });

  const freeSeats = isPartner || isRoundTrip
    ? Number(connection?.free_seats ?? 50)
    : seats.filter((s) => s.status !== "taken").length;
  const maxPassengers = Math.max(freeSeats, passengerData.length);
  const canAddPassenger = passengerData.length < maxPassengers;
  const canRemovePassenger = (index) => index > 0 && passengerData.length > 1;
  const childWithoutAdult = effectiveCounts.children > 0 && effectiveCounts.adults === 0;

  const handlePhoneBlur = () => setPhoneError(!matchIsValidTel(buyerPhone));
  const handleEmailBlur = () => setEmailError(!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(buyerEmail));

  const handleChange = (index, field, value) => {
    if (field !== "name" && field !== "surname") return;
    const formattedValue = value
      .replace(/[^a-zA-Zа-яА-ЯіІїЇєЄґҐ' -]/g, '')
      .replace(/^\s+/, '')
      .replace(/^./, (char) => char.toUpperCase());
    const updatedData = [...passengerData];
    updatedData[index][field] = formattedValue;
    setPassengerData(updatedData);
  };

  const handleChangeType = (index, type) => {
    const updatedData = [...passengerData];
    updatedData[index].type = type;
    if (type !== "child") updatedData[index].birthDate = null;
    setPassengerData(updatedData);
    const updatedErrors = [...passengerErrors];
    if (type !== "child") updatedErrors[index].birthDate = false;
    setPassengerErrors(updatedErrors);
  };

  const handleChangeBirthDate = (index, value) => {
    const updatedData = [...passengerData];
    updatedData[index].birthDate = value;
    setPassengerData(updatedData);
    const updatedErrors = [...passengerErrors];
    updatedErrors[index].birthDate = !value && passengerData[index].type === "child";
    setPassengerErrors(updatedErrors);
  };

  const addPassenger = () => {
    if (!canAddPassenger) return;
    setPassengerData((prev) => [...prev, { name: "", surname: "", type: "adult", birthDate: null }]);
    setPassengerErrors((prev) => [...prev, { name: false, surname: false, birthDate: false }]);
  };

  const removePassenger = (index) => {
    if (index === 0 || passengerData.length <= 1) return;
    setPassengerData((prev) => prev.filter((_, i) => i !== index));
    setPassengerErrors((prev) => prev.filter((_, i) => i !== index));
  };

  const validatePassengerField = (index, field) => {
    const value = passengerData[index][field].trim();
    const updatedErrors = [...passengerErrors];
    updatedErrors[index][field] = value.length < 2;
    if (field === "name" || field === "surname") {
      const p = passengerData[index];
      updatedErrors[index].birthDate = p.type === "child" && !p.birthDate;
    }
    setPassengerErrors(updatedErrors);
  };

  const resetError = (index, field) => {
    const updatedErrors = [...passengerErrors];
    updatedErrors[index][field] = false;
    setPassengerErrors(updatedErrors);
  };

  const handleBookingAndPayment = async () => {
    setIsBookingPending(true);
    if (!bookingIdempotencyKeyRef.current) {
      bookingIdempotencyKeyRef.current =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `idem-${Date.now()}`;
    }
    const idempotencyKey = bookingIdempotencyKeyRef.current;

    const passengersPayload = passengerData.map((p) => {
        const effective = getEffectiveType(p);
        return {
          name: p.name.trim(),
          surname: p.surname.trim(),
          type: effective,
          birth_date: effective === "child" && p.birthDate ? dayjs(p.birthDate).format("YYYY-MM-DD") : null,
        };
      });

    const bookingData = isPartner
      ? {
          connection,
          from_city: fromCity,
          to_city: toCity,
          passengers: passengersPayload,
          buyer_email: buyerEmail,
          buyer_phone: buyerPhone.replace(/\s+/g, ""),
          payment_method: paymentMethod,
        }
      : isRoundTrip
      ? {
          outbound: outbound.itinerary?.legs
            ? {
                legs: outbound.itinerary.legs.map((leg) => ({
                  trip_id: leg.trip.id,
                  from_city: leg.from_city?.name ?? leg.from_city,
                  to_city: leg.to_city?.name ?? leg.to_city,
                })),
              }
            : {
                trip_id: outbound.trip.id,
                from_city: outbound.fromCity,
                to_city: outbound.toCity,
              },
          return: returnTrip.itinerary?.legs
            ? {
                legs: returnTrip.itinerary.legs.map((leg) => ({
                  trip_id: leg.trip.id,
                  from_city: leg.from_city?.name ?? leg.from_city,
                  to_city: leg.to_city?.name ?? leg.to_city,
                })),
              }
            : {
                trip_id: returnTrip.trip.id,
                from_city: returnTrip.fromCity,
                to_city: returnTrip.toCity,
              },
          passengers: passengersPayload.map(({ seat, ...rest }) => rest),
          buyer_email: buyerEmail,
          buyer_phone: buyerPhone.replace(/\s+/g, ""),
          payment_method: paymentMethod,
        }
      : isTransfer
      ? {
          legs: itinerary.legs.map((leg) => ({
            trip_id: leg.trip.id,
            from_city: leg.from_city?.name ?? leg.from_city,
            to_city: leg.to_city?.name ?? leg.to_city,
          })),
          passengers: passengersPayload,
          buyer_email: buyerEmail,
          buyer_phone: buyerPhone.replace(/\s+/g, ""),
          payment_method: paymentMethod,
          ...(appliedPromo?.promo_code
            ? { promo_code: appliedPromo.promo_code }
            : {}),
        }
      : {
          trip_id: trip.id,
          from_city: fromCity,
          to_city: toCity,
          adults: effectiveCounts.adults,
          children: effectiveCounts.children,
          passengers: passengersPayload,
          buyer_email: buyerEmail,
          buyer_phone: buyerPhone.replace(/\s+/g, ''),
          payment_method: paymentMethod,
          ...(appliedPromo?.promo_code
            ? { promo_code: appliedPromo.promo_code }
            : {}),
        };

    const formState = {
      passengers: passengerData.map((p) => ({
        ...p,
        birthDate: p.birthDate ? dayjs(p.birthDate).toISOString() : null,
      })),
      email: buyerEmail,
      phone: buyerPhone,
    };

    const encoded = encodeBase64Unicode(JSON.stringify(formState));
    navigate(`${location.pathname}?form=${encoded}`, { replace: true, state: location.state });

    try {
      const bookingResult = isPartner
        ? await bookPartnerTicket(bookingData, { idempotencyKey })
        : isRoundTrip
          ? await bookRoundTrip(bookingData, { idempotencyKey })
        : isTransfer
          ? await bookItinerary(bookingData, { idempotencyKey })
          : await bookTicket(bookingData, { idempotencyKey });
      setBookingId(bookingResult.booking_id);
      bookingIdempotencyKeyRef.current = null;

      if (paymentMethod === "card") {
        // 2) онлайн-оплата через LiqPay (твій існуючий флоу)
        const paymentData = await createPayment(bookingResult.booking_id);

        const form = document.createElement("form");
        form.method = "POST";
        form.action = "https://www.liqpay.ua/api/3/checkout/";

        const inputData = document.createElement("input");
        inputData.type = "hidden";
        inputData.name = "data";
        inputData.value = paymentData.data;

        const inputSignature = document.createElement("input");
        inputSignature.type = "hidden";
        inputSignature.name = "signature";
        inputSignature.value = paymentData.signature;

        form.appendChild(inputData);
        form.appendChild(inputSignature);
        document.body.appendChild(form);
        form.submit();
      } else {
        // 2) оплата на місці — без створення платежу, ведемо на сторінку бронювання
        navigate(`/booking/${bookingResult.booking_id}`);
      }
    } catch (error) {
      const data = error?.response?.data;
      let message = t("checkout.bookingFailed");
      if (data?.error === "hold_quota_exceeded") {
        message =
          data.reason === "ip"
            ? t("checkout.holdQuotaIpExceeded")
            : t("checkout.holdQuotaExceeded");
      } else if (typeof data?.details === "string" && data.details.trim()) {
        message = data.details;
      } else if (typeof data?.error === "string" && data.error.trim()) {
        message = data.error;
      }
      setErrorDialogText(message);
      setOpenErrorDialog(true);
    } finally {
      setIsBookingPending(false);
    }
  };

  if (!isPaymentEnabled) {
    return (
      <Box sx={{ maxWidth: "600px", margin: "auto", padding: 4, mt: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2, color: "primary.main" }}>
          {t("checkoutDisabled.title")}
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {t("checkoutDisabled.body")}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
          {t("checkoutDisabled.phone")} <a href="tel:+380508626540" style={{ color: "#1E88E5", textDecoration: "none" }}>+38 (050) 862-65-40</a>
        </Typography>
      </Box>
    );
  }

  const actionNote =
    paymentMethod === "card"
      ? t("checkout.paymentOnline")
      : t("checkout.paymentOnSite");

  return (
    <Box sx={{ maxWidth: "95%", margin: "auto", padding: "20px", mt: 2 }}>
      {/* Назад */}
      <Button 
        startIcon={<ArrowBackIcon />} 
        onClick={() => navigate(-1)}
        sx={{ color: "#1E88E5", fontWeight: "bold", textTransform: "none", mb: 2 }}
      >
        {t("checkout.back")}
      </Button>

      <Typography
        variant="h4"
        sx={{
          fontWeight: "bold",
          mb: isRoundTrip && (outboundSummary?.isTransfer || returnSummary?.isTransfer) ? 0.75 : 3,
        }}
      >
        {isPartner
          ? t("checkout.trip", {
              from: connection.from || fromCity,
              to: connection.to || toCity,
            })
          : isRoundTrip
            ? t("checkout.roundTripTitle", {
                from: getCityDisplay(outbound.fromCity) || outbound.fromCity,
                to: getCityDisplay(outbound.toCity) || outbound.toCity,
              })
          : isTransfer
            ? t("checkout.transferTrip", {
                from: getCityDisplay(fromCity) || fromCity,
                to: getCityDisplay(toCity) || toCity,
              })
            : t("checkout.trip", {
                from: getCityDisplay(firstStop?.city) || fromCity,
                to: getCityDisplay(lastStop?.city) || toCity,
              })}
      </Typography>
      {isRoundTrip && (outboundSummary?.isTransfer || returnSummary?.isTransfer) && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t("checkout.withTransfer")}
        </Typography>
      )}

      {isRoundTrip && (
        <Box
          sx={{
            mb: 3,
            p: { xs: 2, sm: 2.5 },
            borderRadius: "12px",
            border: "1px solid #E0E0E0",
            backgroundColor: "#FFFFFF",
          }}
        >
          <DirectionJourneySummary
            label={t("roundTrip.outbound")}
            summary={outboundSummary}
          />
          <Divider sx={{ my: 2.5 }} />
          <DirectionJourneySummary
            label={t("roundTrip.return")}
            summary={returnSummary}
          />
        </Box>
      )}

      {isPartner ? (
        <Box sx={{ mb: 1 }}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} sm={6} md={3} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CalendarTodayIcon sx={{ color: "#D32F2F" }} />
              <Typography sx={{ fontSize: "12px" }}>
                {connection.date}
                {connection.arrival_date ? ` - ${connection.arrival_date}` : ""}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AccessTimeIcon />
              <Typography sx={{ fontSize: "12px" }}>
                {t("checkout.duration")}: {totalDurationText}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {partnerLogo ? (
                <Box
                  component="img"
                  src={partnerLogo}
                  alt=""
                  sx={{ width: 28, height: 28, objectFit: "contain" }}
                />
              ) : (
                <DirectionsBusIcon fontSize="small" />
              )}
              <Typography sx={{ fontSize: "12px" }}>
                {t("checkout.carrier")}: {partnerCarrierName}
              </Typography>
              {partnerRating != null && partnerRating !== "" && (
                <Chip
                  size="small"
                  icon={<StarIcon sx={{ fontSize: 14 }} />}
                  label={partnerRating}
                  sx={{ height: 22, fontSize: "0.7rem" }}
                />
              )}
            </Grid>
            {connection.free_seats != null && (
              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
                  {t("checkout.freeSeats", { count: connection.free_seats })}
                </Typography>
              </Grid>
            )}
          </Grid>
          {connection.change_info && (
            <Box
              sx={{
                mt: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
                py: 1,
                px: 1.5,
                borderRadius: 1,
                bgcolor: "action.hover",
              }}
            >
              <SwapHorizIcon color="primary" fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {connection.change_info}
              </Typography>
            </Box>
          )}
        </Box>
      ) : isTransfer ? (
        <Box sx={{ mb: 2 }}>
          {[leg1, leg2].map((leg, idx) => {
            if (!leg) return null;
            const legStops = adjustTimes(dayjs(leg.trip.departure_date), leg.trip.route?.stops ?? []);
            const fromIdx = legStops.findIndex((s) => cityMatchesSearch(s.city, leg.from_city));
            const toIdx = legStops.findIndex((s) => cityMatchesSearch(s.city, leg.to_city));
            const fromStop = legStops[fromIdx];
            const toStop = legStops[toIdx];
            return (
              <Card key={idx} sx={{ mb: 2, border: "1px solid #E0E0E0" }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                    {idx === 0 ? t("checkout.leg1") : t("checkout.leg2")}: {getCityDisplay(leg.from_city)} → {getCityDisplay(leg.to_city)}
                  </Typography>
                  {fromStop && toStop && getStopDepartureDatetime(fromStop) && getStopArrivalDatetime(toStop) && (
                    <Typography variant="body2" color="text.secondary">
                      {getStopDepartureDatetime(fromStop).format("DD.MM.YYYY HH:mm")} — {getStopArrivalDatetime(toStop).format("DD.MM.YYYY HH:mm")}
                    </Typography>
                  )}
                  {leg.trip?.route?.carrier && (
                    <Typography variant="body2" sx={{ mt: 0.75, fontSize: "12px" }}>
                      {t("checkout.carrier")}: {leg.trip.route.carrier}
                    </Typography>
                  )}
                  {idx === 0 && itinerary.layover_minutes > 0 && (
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                      {t("transferCard.layoverIn", {
                        city: getCityDisplay(itinerary.hub_city),
                        duration: `${Math.floor(itinerary.layover_minutes / 60)}:${String(itinerary.layover_minutes % 60).padStart(2, "0")}`,
                      })}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      ) : isRoundTrip ? null : (
      <Box>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item xs={12} sm={6} md={2} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CalendarTodayIcon sx={{ color: "#D32F2F" }} />
            <Typography sx={{fontSize: '12px'}} >{departureDateTime.format("DD.MM.YYYY")} - {getStopArrivalDatetime(lastStop)?.format("DD.MM.YYYY")}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccessTimeIcon />
            <Typography sx={{fontSize: '12px'}}>{t("checkout.duration")}: {totalDurationText}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{fontSize: '12px'}}>{t("checkout.carrier")}: {brand.name}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DirectionsBusIcon />
            <Typography sx={{fontSize: '12px'}}>{t("checkout.bus")}: {trip.bus.name}</Typography>
          </Grid>
        </Grid>
      </Box>
      )}

      {/* Основний контент */}
      <Box sx={{ mt: 4 }}>
        <Grid container spacing={3}>
          {/* Ліва частина */}
          <Grid item xs={12} md={8}>
            {/* Пасажири */}
            {passengerData?.map((passenger, index) => {
              const childAge = passenger.type === "child" && passenger.birthDate ? getAgeAtTrip(passenger.birthDate) : null;
              return (
              <Card key={index} sx={{ mb: 3, backgroundColor: "#FFFFFF", color: "#000", padding: 3, borderRadius: "12px", border: "1px solid #E0E0E0" }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      {t("checkout.passengerN", { n: index + 1 })}
                    </Typography>
                    {canRemovePassenger(index) && (
                      <IconButton
                        aria-label={t("checkout.removePassenger")}
                        onClick={() => removePassenger(index)}
                        size="small"
                        sx={{ color: "#d32f2f" }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    )}
                  </Box>

                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>{t("checkout.passengerType")}</Typography>
                    <RadioGroup
                      row
                      value={index === 0 ? "adult" : (passenger.type || "adult")}
                      onChange={(e) => index > 0 && handleChangeType(index, e.target.value)}
                    >
                      <FormControlLabel
                        value="adult"
                        control={<Radio size="small" />}
                        label={t("checkout.adult")}
                      />
                      <FormControlLabel
                        value="child"
                        control={<Radio size="small" />}
                        label={t("checkout.child")}
                        disabled={index === 0}
                      />
                    </RadioGroup>
                    {index === 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                        {t("checkout.firstPassengerNote")}
                      </Typography>
                    )}
                  </FormControl>

                  {passenger.type === "child" && (
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="uk">
                      <DatePicker
                        label={t("checkout.childBirthDate")}
                        value={passenger.birthDate}
                        onChange={(value) => handleChangeBirthDate(index, value)}
                        maxDate={dayjs()}
                        slotProps={{ textField: { fullWidth: true, required: true, error: passengerErrors[index]?.birthDate, helperText: passengerErrors[index]?.birthDate ? t("checkout.childBirthRequired") : "" } }}
                        sx={{ mb: childAge == null ? 2 : 1 }}
                      />
                    </LocalizationProvider>
                  )}
                  {childAge != null && (
                    <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                      {childAge < 6 ? t("checkout.childDiscount") : t("checkout.childFullFare")}
                    </Typography>
                  )}

                  <Grid container spacing={1} sx={{ pb: 1 }}>
                    <Grid item xs={12} sm={6} md={6} sx={{ p: 1 }}>
                      <TextField 
                        label={t("checkout.firstName")} 
                        fullWidth
                        variant="outlined" 
                        value={passenger.name} 
                        onChange={(e) => handleChange(index, "name", e.target.value)}
                        onBlur={() => validatePassengerField(index, "name")}
                        onFocus={() => resetError(index, "name")}
                        required
                        error={passengerErrors[index]?.name}
                        helperText={passengerErrors[index]?.name ? t("checkout.nameMin2") : ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6} sx={{ p: 1 }}>
                      <TextField
                        label={t("checkout.lastName")} 
                        fullWidth 
                        variant="outlined" 
                        value={passenger.surname} 
                        onChange={(e) => handleChange(index, "surname", e.target.value)}
                        onBlur={() => validatePassengerField(index, "surname")}
                        onFocus={() => resetError(index, "surname")}
                        required
                        error={passengerErrors[index]?.surname}
                        helperText={passengerErrors[index]?.surname ? t("checkout.surnameMin2") : ""}
                      />
                    </Grid>
                  </Grid>

                  {!isPartner && <BaggageOptions />}

                  {/* Місця — залишив закоментовано як у тебе */}
                  {/* ... */}
                </CardContent>
              </Card>
            );})}

            {canAddPassenger && (
              <Button
                startIcon={<AddIcon />}
                onClick={addPassenger}
                variant="outlined"
                sx={{ mb: 3, color: "#1E88E5", borderColor: "#1E88E5" }}
              >
                {t("checkout.addPassenger")}
              </Button>
            )}

            {/* Покупець */}
            <Card sx={{ mb: 3, backgroundColor: "#FFFFFF", color: "#000", padding: 3, borderRadius: "12px", border: "1px solid #E0E0E0" }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                  {t("checkout.buyerInfo")}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: "#757575" }}>
                  {t("checkout.buyerInfoNote")}
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6} md={6} sx={{ p: 1 }}>
                    <MuiTelInput
                      label={t("checkout.yourPhone")}
                      value={buyerPhone}
                      onChange={setBuyerPhone}
                      defaultCountry="UA"
                      fullWidth
                      required
                      variant="outlined"
                      onBlur={handlePhoneBlur}
                      error={phoneError}
                      helperText={phoneError ? t("checkout.invalidPhone") : ""}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6} sx={{ p: 1 }}>
                    <TextField 
                      label={t("checkout.yourEmail")} 
                      fullWidth 
                      variant="outlined" 
                      required
                      value={buyerEmail} 
                      disabled={user?.email}
                      onChange={(e) => setBuyerEmail(e.target.value)}
                      onBlur={handleEmailBlur}
                      error={emailError}
                      helperText={emailError ? t("auth.invalidEmail") : ""}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3, backgroundColor: "#FFFFFF", color: "#000", padding: 3, borderRadius: "12px", border: "1px solid #E0E0E0" }}>
              <CardContent>
                <FormControl>
                  <FormLabel sx={{ fontWeight: "bold", color: "#1F2937", mb: 1 }}>{t("checkout.paymentMethod")}</FormLabel>
                  <RadioGroup
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    name="payment-method"
                  >
                    {cardAvailable ? (
                    <FormControlLabel
                      value="card"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CreditCardIcon fontSize="small" />
                          <Typography>{t("checkout.card")}</Typography>
                        </Box>
                      }
                    />
                    ) : null}
                    <FormControlLabel
                      value="on_site"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <StorefrontIcon fontSize="small" />
                          <Typography>{t("checkout.cashOnBoarding")}</Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>

          {/* Права частина */}
          <Grid item xs={12} md={4}>
            {/* Про рейс */}
            <Card sx={{ mb: 3, backgroundColor: "#FFFFFF", color: "#000", padding: 3, borderRadius: "12px", border: "1px solid #E0E0E0" }}>
              <CardContent>
                <Box sx={{ color: "#111827" }}>
                  <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, color: "#1F2937" }}>
                    {t("checkout.aboutTrip")}
                  </Typography>

                  {isPartner ? (
                    <Box>
                      {partnerDetailsLoading && partnerSchedule.length === 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Skeleton height={36} />
                          <Skeleton height={36} />
                          <Skeleton height={36} />
                        </Box>
                      )}

                      {(partnerSchedule.length > 0
                        ? showAllStops
                          ? partnerSchedule
                          : [partnerSchedule[0], partnerSchedule[partnerSchedule.length - 1]]
                        : [
                            {
                              city: connection.from,
                              station: connection.departure_station,
                              time: connection.departure_time,
                              date: connection.date,
                            },
                            {
                              city: connection.to,
                              station: connection.arrival_station,
                              time: connection.arrival_time,
                              date: connection.arrival_date || connection.date,
                            },
                          ]
                      ).map((stop, index, arr) => {
                        const isLast = index === arr.length - 1;
                        const stopDate =
                          stop.date ||
                          (isLast
                            ? connection.arrival_date || connection.date
                            : index === 0
                              ? connection.date
                              : "");
                        return (
                          <Box key={`${stop.city}-${stop.time}-${index}`}>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                              <Box minWidth="75px">
                                <Typography
                                  variant="h5"
                                  sx={{ fontWeight: "bold", color: "primary.main", mr: 1 }}
                                >
                                  {stop.time || "—"}
                                </Typography>
                                {stopDate && (
                                  <Typography sx={{ fontSize: "12px", color: "#000", mr: 1 }}>
                                    {stopDate}
                                  </Typography>
                                )}
                              </Box>
                              <CircleIcon sx={{ fontSize: 12, color: "primary.main", mr: 1 }} />
                              <Box>
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: "bold", color: "#111827" }}
                                >
                                  {stop.city}
                                </Typography>
                                {stop.station && (
                                  <Typography variant="body2" sx={{ color: "#6B7280" }}>
                                    <MapAddressLink city={stop.city} address={stop.station} />
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            {!isLast && (
                              <Box
                                sx={{
                                  borderLeft: `2px dashed ${BRAND.primary}`,
                                  height: "20px",
                                  ml: "12px",
                                  mb: 1,
                                }}
                              />
                            )}
                          </Box>
                        );
                      })}

                      {intermediateStopsCount > 0 && (
                        <Button
                          onClick={toggleStops}
                          sx={{
                            color: "#2563EB",
                            fontWeight: "bold",
                            fontSize: "14px",
                            textTransform: "none",
                            mt: 1,
                          }}
                        >
                          {showAllStops
                            ? t("checkout.hideStops")
                            : t("checkout.showStops", { count: intermediateStopsCount })}
                        </Button>
                      )}

                      <Typography sx={{ fontSize: "14px", color: "#6B7280", mt: 1 }}>
                        {t("checkout.timeEnRoute")}: {totalDurationText}
                      </Typography>

                      {connection.change_info && (
                        <Box
                          sx={{
                            mt: 1.5,
                            py: 1,
                            px: 1.5,
                            borderRadius: 1,
                            bgcolor: "action.hover",
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {connection.change_info}
                          </Typography>
                        </Box>
                      )}

                      {partnerAmenities.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, mb: 1, color: "#374151" }}
                          >
                            {t("checkout.amenities")}
                          </Typography>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                            {partnerAmenities.map((amenity) => (
                              <Chip
                                key={amenity}
                                size="small"
                                label={t(`checkout.amenity.${amenity}`, {
                                  defaultValue: amenity,
                                })}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {partnerBaggage && (
                        <Box sx={{ mt: 2 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 700,
                              mb: 0.5,
                              color: "#374151",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <LuggageIcon fontSize="small" />
                            {t("checkout.baggageRules")}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {partnerBaggage}
                          </Typography>
                        </Box>
                      )}

                      {partnerComfLink && (
                        <Link
                          href={partnerComfLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ display: "inline-block", mt: 1.5, fontSize: 14 }}
                        >
                          {t("checkout.carrierRules")}
                        </Link>
                      )}

                      {connection.price_label && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          {connection.price_label}
                        </Alert>
                      )}
                    </Box>
                  ) : isRoundTrip ? (
                    <Box sx={{ mx: -1 }}>
                      {[
                        {
                          key: "outbound",
                          label: t("roundTrip.outbound"),
                          sel: outbound,
                          summary: outboundSummary,
                          defaultExpanded: true,
                        },
                        {
                          key: "return",
                          label: t("roundTrip.return"),
                          sel: returnTrip,
                          summary: returnSummary,
                          defaultExpanded: false,
                        },
                      ].map(({ key, label, sel, summary, defaultExpanded }) => {
                        const durationLabel = formatDurationHhMm(
                          summary?.totalDurationMinutes
                        );
                        return (
                          <Accordion
                            key={key}
                            defaultExpanded={defaultExpanded}
                            disableGutters
                            elevation={0}
                            sx={{
                              "&:before": { display: "none" },
                              borderBottom: "1px solid #E5E7EB",
                              backgroundColor: "transparent",
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{ px: 1, minHeight: 48 }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  flexWrap: "wrap",
                                  width: "100%",
                                  pr: 1,
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  sx={{ fontWeight: 700, color: "#1F2937" }}
                                >
                                  {label}
                                </Typography>
                                {durationLabel && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontVariantNumeric: "tabular-nums" }}
                                  >
                                    {t("units.hoursShort", { duration: durationLabel })}
                                  </Typography>
                                )}
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 1, pt: 0, pb: 2 }}>
                              {summary?.isTransfer && sel?.itinerary?.legs?.length
                                ? (sel.itinerary.legs || []).map((leg, idx) => (
                                    <Box key={`${key}-${idx}`} sx={{ mb: 1.5 }}>
                                      <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 700, mb: 1, color: "#374151" }}
                                      >
                                        {idx === 0
                                          ? t("checkout.leg1")
                                          : t("checkout.leg2")}
                                        : {getCityDisplay(leg.from_city)} →{" "}
                                        {getCityDisplay(leg.to_city)}
                                      </Typography>
                                      {leg.trip?.route?.carrier && (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ mb: 1, fontSize: "0.8125rem" }}
                                        >
                                          {t("checkout.carrier")}: {leg.trip.route.carrier}
                                        </Typography>
                                      )}
                                      {renderVisibleStops(getLegStopsSlice(leg)?.slice)}
                                      {idx === 0 &&
                                        sel.itinerary.layover_minutes > 0 && (
                                          <Box
                                            sx={{
                                              my: 1.5,
                                              py: 1,
                                              px: 1.5,
                                              borderRadius: 1,
                                              bgcolor: "action.hover",
                                            }}
                                          >
                                            <Typography
                                              variant="body2"
                                              sx={{ fontWeight: 600, color: "#374151" }}
                                            >
                                              {t("transferCard.layoverIn", {
                                                city: getCityDisplay(
                                                  sel.itinerary.hub_city || leg.to_city
                                                ),
                                                duration: formatHumanLayover(
                                                  sel.itinerary.layover_minutes,
                                                  t
                                                ),
                                              })}
                                            </Typography>
                                          </Box>
                                        )}
                                    </Box>
                                  ))
                                : renderVisibleStops(summary?.stopsSlice)}
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </Box>
                  ) : isTransfer ? (
                    <>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: "#374151" }}>
                        {t("checkout.leg1")}: {getCityDisplay(leg1?.from_city)} → {getCityDisplay(leg1?.to_city)}
                      </Typography>
                      {leg1?.trip?.route?.carrier && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: "0.8125rem" }}>
                          {t("checkout.carrier")}: {leg1.trip.route.carrier}
                        </Typography>
                      )}
                      {renderVisibleStops(leg1StopsSlice?.slice)}

                      {itinerary.layover_minutes > 0 && (
                        <Box
                          sx={{
                            my: 1.5,
                            py: 1,
                            px: 1.5,
                            borderRadius: 1,
                            bgcolor: "action.hover",
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#374151" }}>
                            {t("transferCard.layoverIn", {
                              city: getCityDisplay(itinerary.hub_city),
                              duration: formatLayoverDuration(itinerary.layover_minutes),
                            })}
                          </Typography>
                        </Box>
                      )}

                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, mt: 2, color: "#374151" }}>
                        {t("checkout.leg2")}: {getCityDisplay(leg2?.from_city)} → {getCityDisplay(leg2?.to_city)}
                      </Typography>
                      {leg2?.trip?.route?.carrier && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: "0.8125rem" }}>
                          {t("checkout.carrier")}: {leg2.trip.route.carrier}
                        </Typography>
                      )}
                      {renderVisibleStops(leg2StopsSlice?.slice)}
                    </>
                  ) : (
                    <>
                      <Box>
                        {!showAllStops && [stops?.[firstStopIndex], stops?.[lastStopIndex]]?.map((stop, index) => {
                          const stopTime = index === 0
                            ? getStopDepartureDatetime(stop)
                            : getStopArrivalDatetime(stop);
                          if (!stopTime) return null;
                          return (
                          <Box key={index}>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                              <Box minWidth='75px'>
                                <Typography variant="h5" sx={{ fontWeight: "bold", color: "primary.main", mr: 1 }}>
                                  {stopTime.format('HH:mm')}
                                </Typography>
                                <Typography sx={{ fontSize: "12px", color: "#000", mr: 1 }}>
                                  {stopTime.format("DD.MM.YYYY")}
                                </Typography>
                              </Box>
                              <CircleIcon sx={{ fontSize: 12, color: "primary.main", mr: 1 }} />
                              <Box>
                                <Typography variant="body1" sx={{ fontWeight: "bold", color: "#111827" }}>{getCityDisplay(stop?.city)}</Typography>
                                {stop?.address && (
                                  <Typography variant="body2" sx={{ color: "#6B7280" }}>
                                    <MapAddressLink city={getCityDisplay(stop.city)} address={stop.address} />
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            {index === 0 && (
                              <Box sx={{ borderLeft: `2px dashed ${BRAND.primary}`, height: "20px", ml: "12px", mb: 1 }} />
                            )}
                          </Box>
                        );})}
                      </Box>

                      {showAllStops && (
                        <Box>
                          {stops.slice(firstStopIndex, lastStopIndex + 1)?.map((stop, index, arr) => {
                            const isLast = index === arr.length - 1;
                            const stopTime = isLast
                              ? getStopArrivalDatetime(stop)
                              : getStopDepartureDatetime(stop);
                            if (!stopTime) return null;
                            return (
                            <Box key={index}>
                              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                <Box minWidth='75px'>
                                  <Typography variant="h5" sx={{ fontWeight: "bold", color: "primary.main", mr: 1 }}>
                                    {stopTime.format('HH:mm')}
                                  </Typography>
                                  <Typography sx={{ fontSize: "12px", color: "#000", mr: 1 }}>
                                    {stopTime.format("DD.MM.YYYY")}
                                  </Typography>
                                </Box>
                                <CircleIcon sx={{ fontSize: 12, color: "primary.main", mr: 1 }} />
                                <Box>
                                  <Typography variant="body1" sx={{ fontWeight: "bold", color: "#111827" }}>
                                    {getCityDisplay(stop.city)}{" "}
                                    {stop.by_transfer && (
                                      <Typography variant="caption" component="span" sx={{ fontSize: 10, ml: 1 }}>
                                        ({t("checkout.transfer")})
                                      </Typography>
                                    )}
                                  </Typography>
                                  {stop.address && (
                                    <Typography variant="body2" sx={{ color: "#6B7280" }}>
                                      <MapAddressLink city={getCityDisplay(stop.city)} address={stop.address} />
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                              {index !== arr.length - 1 && (
                                <Box sx={{ borderLeft: `2px dashed ${BRAND.primary}`, height: "20px", ml: "12px", mb: 1 }} />
                              )}
                            </Box>
                          );})}
                        </Box>
                      )}
                    </>
                  )}

                  {!isPartner && (
                    <>
                      <Divider sx={{ backgroundColor: "#D1D5DB", my: 2 }} />

                      {childDiscountUah > 0 && (
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 1 }}>
                          <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "success.main" }}>
                            {t("checkout.childDiscount")}
                          </Typography>
                          <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "success.main" }}>
                            −{t("units.uahAmount", { amount: childDiscountUah.toLocaleString() })}
                          </Typography>
                        </Box>
                      )}

                      {intermediateStopsCount > 0 && (
                        <Button onClick={toggleStops} sx={{ color: "#2563EB", fontWeight: "bold", fontSize: "14px", textTransform: "none" }}>
                          {showAllStops ? t("checkout.hideStops") : t("checkout.showStops", { count: intermediateStopsCount })}
                        </Button>
                      )}
                      {!isRoundTrip && (
                      <Typography sx={{ fontSize: "14px", color: "#6B7280", mt: 1 }}>
                        {t("checkout.timeEnRoute")}: {totalDurationText}
                      </Typography>
                      )}

                      {ownAmenities.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, mb: 1, color: "#374151" }}
                          >
                            {t("checkout.amenities")}
                          </Typography>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                            {ownAmenities.map((amenity) => (
                              <Chip
                                key={amenity}
                                size="small"
                                label={t(`checkout.amenity.${amenity}`, {
                                  defaultValue: amenity,
                                })}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Оплата/Підсумок */}
            <Card sx={{ mb: 1, backgroundColor: "#FFFFFF", color: "#000", padding: 3, borderRadius: "12px", border: "1px solid #E0E0E0" }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: "bold", color: "#1F2937" }}>{t("checkout.toPay")}</Typography>
                {!isPartner && !isRoundTrip && (
                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                      {t("checkout.promoCode")}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={promoInput}
                        onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                        placeholder={t("checkout.promoPlaceholder")}
                        disabled={Boolean(appliedPromo)}
                      />
                      {appliedPromo ? (
                        <Button variant="outlined" onClick={handleClearPromo}>
                          {t("checkout.promoClear")}
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          onClick={handleApplyPromo}
                          disabled={promoLoading || !promoInput.trim()}
                        >
                          {t("checkout.promoApply")}
                        </Button>
                      )}
                    </Box>
                    {promoError && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                        {promoError}
                      </Typography>
                    )}
                    {appliedPromo && (
                      <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: "block" }}>
                        {t("checkout.promoApplied", { code: appliedPromo.promo_code })}
                      </Typography>
                    )}
                  </Box>
                )}
                {isRoundTrip ? (
                  <>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1, mt: 1 }}>
                      <Typography variant="body1">{t("checkout.outboundPrice")}</Typography>
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        {t("units.uahAmount", { amount: rtOutboundAmount.toLocaleString() })}
                      </Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1, mt: 1 }}>
                      <Typography variant="body1">{t("checkout.returnPrice")}</Typography>
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        {t("units.uahAmount", { amount: rtReturnAmount.toLocaleString() })}
                      </Typography>
                    </Box>
                    <Divider />
                    {(effectiveCounts.adults > 0 || effectiveCounts.children > 0) && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                        {t("common.adults")}: {effectiveCounts.adults}
                        {effectiveCounts.children > 0
                          ? ` · ${t("common.childrenUnder6")}: ${effectiveCounts.children}`
                          : ""}
                      </Typography>
                    )}
                  </>
                ) : (
                  <>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1, mt: 1 }}>
                      <Typography variant="body1">{t("common.adults")}: {effectiveCounts.adults}</Typography>
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        {t("units.uahAmount", { amount: (effectiveCounts.adults * shownAdultUnit).toLocaleString() })}
                      </Typography>
                    </Box>
                    <Divider />
                    {effectiveCounts.children > 0 && (
                      <>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1, mt: 1 }}>
                          <Typography variant="body1">{t("common.childrenUnder6")}: {effectiveCounts.children}</Typography>
                          <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                            {t("units.uahAmount", { amount: (effectiveCounts.children * shownChildUnit).toLocaleString() })}
                          </Typography>
                        </Box>
                        <Divider />
                      </>
                    )}
                  </>
                )}
                {fullPriceAmount != null && (campaignSavings > 0 || promoWins) && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1, mt: 1 }}>
                    <Typography variant="body1">{t("checkout.fullPrice")}</Typography>
                    <Typography
                      variant="body1"
                      sx={{ textDecoration: "line-through", color: "text.secondary" }}
                    >
                      {t("units.uahAmount", { amount: fullPriceAmount })}
                    </Typography>
                  </Box>
                )}
                {directHasCampaign && trip?.fare?.campaign_online_only && paymentMethod !== "card" && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t("checkout.onlineOffer")}
                  </Typography>
                )}
                {campaignSavings > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1, mt: 1 }}>
                    <Typography variant="body1">
                      {t("checkout.campaignDiscount", {
                        name: trip?.fare?.campaign_label || "",
                      })}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: "bold", color: "error.main" }}>
                      −{t("units.uahAmount", { amount: campaignSavings })}
                    </Typography>
                  </Box>
                )}
                {promoDiscount > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1, mt: 1 }}>
                    <Typography variant="body1">{t("checkout.promoDiscount")}</Typography>
                    <Typography variant="body1" sx={{ fontWeight: "bold", color: "success.main" }}>
                      −{t("units.uahAmount", { amount: promoDiscount })}
                    </Typography>
                  </Box>
                )}
                <Typography variant="h5" sx={{ fontWeight: "bold", color: "primary.main", mt: 2 }}>
                  {t("checkout.total")}: {t("units.uahAmount", { amount: totalAmount })}
                </Typography>
              </CardContent>
            </Card>

            {/* Примітка до дії та кнопка */}
            {childWithoutAdult && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t("checkout.childNeedsAdult")}
              </Alert>
            )}
            <Typography sx={{ fontSize: 12, color: "#6B7280", mb: 1 }}>
              {actionNote}
            </Typography>
            {isPartner && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {t("checkout.partnerDisclaimerTitle")}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t("checkout.partnerDisclaimerBody")}
                </Typography>
                <Typography
                  component="button"
                  type="button"
                  onClick={() => navigate("/offer")}
                  sx={{
                    border: 0,
                    background: "none",
                    p: 0,
                    color: "#1E88E5",
                    fontWeight: 700,
                    cursor: "pointer",
                    fontSize: 14,
                    textDecoration: "underline",
                  }}
                >
                  {t("checkout.partnerDisclaimerOfferLink")}
                </Typography>
                {partnerComfLink ? (
                  <>
                    {" · "}
                    <Typography
                      component="a"
                      href={partnerComfLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: "#1E88E5", fontWeight: 700, fontSize: 14 }}
                    >
                      {t("checkout.carrierRules")}
                    </Typography>
                  </>
                ) : null}
              </Alert>
            )}
            <PaymentButton
            onPay={handleBookingAndPayment}
            disabled={isPaymentDisabled || isBookingPending}
            paymentMethod={paymentMethod}
            isPartner={isPartner}
            />
          </Grid>
        </Grid>
      </Box>

      <Dialog open={openErrorDialog} onClose={() => setOpenErrorDialog(false)}>
        <DialogTitle sx={{ fontWeight: "bold" }}>{t("checkout.error")}</DialogTitle>
        <DialogContent>
          <Typography>{errorDialogText}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenErrorDialog(false)} autoFocus>
            {t("checkout.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CheckoutPage;
